import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_archive-democracy/artifacts/wo870loq_New%20School%20Logo.jpg";

export default function Home() {
  const { t } = useLang();
  return (
    <div>
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20" data-testid="home-hero">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7">
            <div className="eyebrow mb-8">{t("home.eyebrow")}</div>
            <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-[#111111]">
              {t("home.title")}
            </h1>
            <p className="font-body text-lg md:text-xl text-[#3A3A3A] leading-relaxed mt-8 max-w-2xl">
              {t("home.intro")}
            </p>

            <div className="mt-10 max-w-2xl" data-testid="home-programs">
              <div className="eyebrow mb-4">Our Programs</div>
              <ul className="space-y-5 font-body text-lg text-[#3A3A3A] leading-relaxed">
                <li>
                  <span className="text-[#111111] font-headline text-xl">The 180 Project</span> is our flagship global scholarship, open to students at every academic level, in every country, in every language. Applicants share their research in a 180-second pitch and a single static slide. Awards are given for clarity, depth of inquiry, and public resonance.
                </li>
                <li>
                  <span className="text-[#111111] font-headline text-xl">The Kitchen Table</span> is our virtual office hours program, a warm, informal space where applicants come with questions, drafts, and doubts and are met with real support. More programs are in development, including public and K–12 courses launching in the year ahead.
                </li>
              </ul>
            </div>

            <div className="mt-10 max-w-2xl border-l-2 border-[#111111] pl-6" data-testid="home-commitment">
              <div className="eyebrow mb-3">Our Commitment</div>
              <p className="font-body text-lg text-[#111111] leading-relaxed italic">
                We do not exist to tear down. We exist to lift up the poor, the overlooked, the unrecognized, and every faithful hand still building something worth building. We are here to help the good grow.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/library" className="btn-primary" data-testid="hero-explore-library">{t("home.exploreArchive")}</Link>
              <Link to="/the-180-project" className="btn-outline" data-testid="hero-180-project">{t("home.the180")}</Link>
            </div>
          </div>
          <div className="lg:col-span-5 hidden lg:flex flex-col items-center justify-center">
            <div className="border border-[#DCDCDC] w-full bg-white flex items-center justify-center p-8">
              <img src={HERO_IMG} alt="The Institute of the New School of Thoughts — logo" className="w-full max-h-[440px] object-contain" />
            </div>
            <div className="mt-3 font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">FIG. 01. Institute Emblem</div>
          </div>
        </div>
      </section>

      {/* Our Work */}
      <section className="border-t border-[#DCDCDC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-16">
          <div className="flex items-baseline justify-between mb-10">
            <div className="eyebrow">{t("home.workEyebrow")}</div>
            <div className="font-ui text-[10px] tracking-[0.25em] uppercase text-[#6C6C6C]">{t("home.workCount")}</div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-[#DCDCDC]">
            {[
              { num: "I", title: t("work1.title"), desc: t("work1.desc"), to: "/the-180-project" },
              { num: "II", title: t("work2.title"), desc: t("work2.desc"), to: "/library" },
              { num: "III", title: t("work3.title"), desc: t("work3.desc"), to: "/youth-leaders" },
            ].map((c, i) => (
              <Link key={c.num} to={c.to} className={`p-8 md:p-12 border-[#DCDCDC] ${i < 2 ? "md:border-r" : ""} border-b md:border-b-0 group`} data-testid={`work-${i}`}>
                <div className="num text-[#DCDCDC] group-hover:text-[#111111] transition-colors">{c.num}</div>
                <h3 className="font-headline text-2xl md:text-3xl mt-6">{c.title}</h3>
                <p className="font-body text-[#3A3A3A] mt-4">{c.desc}</p>
                <div className="mt-8 inline-flex items-center gap-2 font-ui text-[11px] tracking-[0.2em] uppercase font-semibold">
                  {t("home.readMore")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto */}
      <section className="border-t border-[#DCDCDC]">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <div className="eyebrow">{t("home.noteEyebrow")}</div>
          </div>
          <div className="md:col-span-8">
            <p className="font-headline text-3xl md:text-4xl leading-tight text-[#111111]">
              {t("home.quote")}
            </p>
            <div className="mt-6 eyebrow">{t("home.quoteAttr")}</div>
          </div>
        </div>
      </section>
    </div>
  );
}
